import React, { useState, useEffect, useMemo } from 'react';
import { FaReceipt, FaPlus, FaCamera, FaCheck, FaTimes, FaFileInvoiceDollar, FaTrash, FaEye, FaDownload, FaFileArchive, FaRobot, FaMagic, FaFilePdf, FaUtensils, FaGasPump, FaHammer, FaEllipsisH } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { formatLocalDate } from '../../utils/timesheetHelpers';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';

const TabExpenses = ({ user, pb, isEmployeeOrAdmin }) => {
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isManualEntry, setIsManualEntry] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [newExpense, setNewExpense] = useState({
        amount: '',
        category: 'repas',
        date: formatLocalDate(new Date()),
        description: '',
        receipt: null
    });

    const isManager = user?.role === 'admin' || user?.role === 'employee';

    const openNewNote = (manual = false) => {
        setIsManualEntry(manual);
        setNewExpense({
            amount: '',
            category: 'repas',
            date: formatLocalDate(new Date()),
            description: '',
            receipt: null
        });
        setShowModal(true);
    };

    const fetchExpenses = async () => {
        setLoading(true);
        try {
            const filter = isManager ? '' : `user = "${user.id}"`;
            const records = await pb.collection('expenses').getFullList({
                filter,
                sort: '-date',
                expand: 'user'
            });
            setExpenses(records);
        } catch (error) {
            console.error('Error fetching expenses:', error);
            toast.error("Erreur lors du chargement des dépenses.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.id) fetchExpenses();
    }, [user?.id, isManager]);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setNewExpense({ ...newExpense, receipt: file });
        setIsAnalyzing(true);

        // --- SIMULATION OCR IA ---
        setTimeout(() => {
            const mockAmounts = ['45.50', '28.90', '112.00', '15.30'];
            const mockCats = ['repas', 'essence', 'materiel'];
            const mockDescs = ['Restaurant Le Relais', 'Station Total Access', 'Leroy Merlin', 'Boulangerie Paul'];

            setNewExpense(prev => ({
                ...prev,
                amount: prev.amount || mockAmounts[Math.floor(Math.random() * mockAmounts.length)],
                category: mockCats[Math.floor(Math.random() * mockCats.length)],
                description: prev.description || mockDescs[Math.floor(Math.random() * mockDescs.length)],
                date: formatLocalDate(new Date())
            }));

            setIsAnalyzing(false);
            toast.success("Analyse IA terminée : Montant et Catégorie extraits ! ✨");
        }, 2500);
    };

    const handleExportZip = async () => {
        if (expenses.length === 0) return toast.info("Aucune donnée à exporter.");

        const zip = new JSZip();
        toast.info("Génération de l'archive comptable...");

        try {
            // 1. Création du PDF Récapitulatif
            const doc = new jsPDF();
            doc.setFontSize(22);
            doc.setTextColor(164, 198, 57);
            doc.text("REPORTING NOTES DE FRAIS", 14, 22);

            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Émis le : ${new Date().toLocaleDateString()}`, 14, 30);
            doc.text(`Collaborateur : ${user.name || user.username}`, 14, 35);

            const tableData = expenses.map(exp => [
                exp.date ? new Date(exp.date).toLocaleDateString() : '?',
                (exp.category || 'fraispas').toUpperCase(),
                exp.description || "-",
                `${(exp.amount || 0).toFixed(2)} €`,
                exp.status === 'approved' ? 'VALIDÉ' : 'EN ATTENTE'
            ]);

            autoTable(doc, {
                startY: 45,
                head: [['Date', 'Catégorie', 'Description', 'Montant', 'Statut']],
                body: tableData,
                headStyles: { fillColor: [164, 198, 57] }
            });

            zip.file("Recapitulatif_Frais.pdf", doc.output('blob'));

            // 2. Ajout des justificatifs
            const imgFolder = zip.folder("justificatifs");

            for (const exp of expenses) {
                if (exp.receipt) {
                    try {
                        const url = pb.files.getURL(exp, exp.receipt);
                        const response = await fetch(url);
                        const blob = await response.blob();
                        const filename = `${formatLocalDate(new Date(exp.date))}_${exp.category}_${exp.amount}EUR.jpg`;
                        imgFolder.file(filename, blob);
                    } catch (e) {
                        console.error("Failed to add image", exp.id);
                    }
                }
            }

            console.log("Generating ZIP archive...");
            const content = await zip.generateAsync({ type: "blob" });
            console.log("ZIP content generated successfully");
            saveAs(content, `Compta_MaisonQuinard_${new Date().getMonth() + 1}_${new Date().getFullYear()}.zip`);
            toast.success("Archive prête ! Vos justificatifs sont organisés. 📂");
        } catch (err) {
            console.error("DETAILED EXPORT ERROR:", err);
            toast.error(`Erreur lors de l'export : ${err.message || 'Problème technique'}`);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newExpense.amount) return toast.error("Le montant est requis.");

        setSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('user', user.id);
            formData.append('amount', newExpense.amount);
            formData.append('category', newExpense.category);
            formData.append('date', newExpense.date);
            formData.append('description', newExpense.description);
            formData.append('status', 'pending');
            if (newExpense.receipt) {
                formData.append('receipt', newExpense.receipt);
            }

            await pb.collection('expenses').create(formData);
            toast.success("Note de frais envoyée pour validation ! 🚀");
            setShowModal(false);
            setNewExpense({
                amount: '',
                category: 'repas',
                date: formatLocalDate(new Date()),
                description: '',
                receipt: null
            });
            fetchExpenses();
        } catch (error) {
            console.error('Submit error:', error);
            toast.error("Erreur lors de l'envoi.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateStatus = async (id, newStatus) => {
        try {
            await pb.collection('expenses').update(id, { status: newStatus });
            toast.success(newStatus === 'approved' ? "Note validée !" : "Note rejetée.");
            fetchExpenses();
        } catch (error) {
            toast.error("Erreur lors de la mise à jour.");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer cette note de frais ?")) return;
        try {
            await pb.collection('expenses').delete(id);
            toast.success("Note supprimée.");
            fetchExpenses();
        } catch (error) {
            toast.error("Erreur lors de la suppression.");
        }
    };

    const currentMonthExpenses = useMemo(() => {
        return expenses.filter(e => {
            const d = new Date(e.date);
            const now = new Date();
            return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
        });
    }, [expenses]);

    const totalAmount = currentMonthExpenses.reduce((acc, curr) => acc + (curr.amount || 0), 0);
    const pendingAmount = expenses.filter(e => e.status === 'pending').reduce((acc, curr) => acc + (curr.amount || 0), 0);
    const approvedCount = currentMonthExpenses.filter(e => e.status === 'approved').length;

    const getStatusColor = (status) => {
        if (status === 'approved') return '#10b981'; // Green
        if (status === 'rejected') return '#ef4444'; // Red
        return '#f59e0b'; // Orange (Pending)
    };

    const getStatusLabel = (status) => {
        if (status === 'approved') return 'Validé';
        if (status === 'rejected') return 'Refusé';
        return 'En attente';
    };

    const getCategoryIcon = (category) => {
        if (category === 'repas') return <FaUtensils />;
        if (category === 'essence') return <FaGasPump />;
        if (category === 'materiel') return <FaHammer />;
        return <FaEllipsisH />;
    };

    return (
        <div className="expenses-page scale-in">
            {/* Header */}
            <div className="w-page-header" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ marginBottom: '20px' }}>
                    <h2 className="w-page-title">
                        {isManager ? "Gestion Notes de Frais" : "Mes Notes de Frais"}
                    </h2>
                    <p className="w-page-subtitle">
                        {isManager ? "Validation et suivi des dépenses équipe" : "Déclarez vos frais de déplacement et repas"}
                    </p>
                </div>

                {/* Stats Grid */}
                <div className="stats-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                    <div className="stat-card" style={{ borderLeftColor: '#3b82f6' }}>
                        <span className="stat-label">Total Mois</span>
                        <div className="stat-value">{totalAmount.toFixed(2)}€</div>
                        <span className="stat-sub">{currentMonthExpenses.length} notes</span>
                    </div>
                    <div className="stat-card" style={{ borderLeftColor: '#10b981' }}>
                        <span className="stat-label">Validées</span>
                        <div className="stat-value" style={{ color: '#10b981' }}>{approvedCount}</div>
                        <span className="stat-sub">Ce mois-ci</span>
                    </div>
                    <div className="stat-card" style={{ borderLeftColor: '#f59e0b' }}>
                        <span className="stat-label">À Valider</span>
                        <div className="stat-value" style={{ color: '#f59e0b' }}>{pendingAmount.toFixed(2)}€</div>
                        <span className="stat-sub">Total en attente</span>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div style={{ padding: '0 20px 20px', display: 'flex', gap: '10px' }}>
                {!isManager && (
                    <>
                        <button className="w-btn-full" onClick={() => openNewNote(false)} style={{ flex: 1, background: 'var(--color-primary)' }}>
                            <FaCamera /> Scan IA Rapide
                        </button>
                        <button className="w-btn-full" onClick={() => openNewNote(true)} style={{ flex: 1, background: '#334155' }}>
                            <FaPlus /> Manuelle
                        </button>
                    </>
                )}
                {isManager && (
                    <button className="w-btn-full" onClick={handleExportZip} style={{ flex: 1, background: '#10b981' }}>
                        <FaFileArchive /> Export ZIP Compta
                    </button>
                )}
            </div>

            {/* Expenses List */}
            <div className="w-cards-list">
                <div className="w-date-header">Historique des Frais</div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Chargement...</div>
                ) : expenses.length === 0 ? (
                    <div className="w-empty-state">
                        <div className="empty-icon"><FaFileInvoiceDollar /></div>
                        <h3>Aucune note de frais</h3>
                        <p>Vous n'avez pas encore déclaré de frais.</p>
                    </div>
                ) : (
                    expenses.map(exp => {
                        const statusColor = getStatusColor(exp.status);
                        
                        return (
                            <div key={exp.id} className="w-card premium" style={{ borderLeft: `4px solid ${statusColor}`, marginBottom: '15px' }}>
                                <div className="w-card-header" style={{ marginBottom: '10px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div className="cat-icon" style={{ background: '#f1f5f9', color: '#64748b', padding: '10px', borderRadius: '12px', fontSize: '18px' }}>
                                            {getCategoryIcon(exp.category)}
                                        </div>
                                        <div>
                                            {isManager && (
                                                <div style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', color: '#3b82f6', marginBottom: '2px' }}>
                                                    {exp.expand?.user?.name || exp.expand?.user?.username}
                                                </div>
                                            )}
                                            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 900, color: '#0f172a' }}>
                                                {exp.description || exp.category}
                                            </h3>
                                            <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 600 }}>
                                                {new Date(exp.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '1.4rem', fontWeight: 900, color: statusColor }}>
                                            {exp.amount.toFixed(2)}€
                                        </div>
                                        <div className="w-badge" style={{ background: `${statusColor}22`, color: statusColor, display: 'inline-block', marginTop: '4px' }}>
                                            {getStatusLabel(exp.status)}
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="w-card-footer" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        {exp.receipt ? (
                                            <button className="w-btn-outline" onClick={() => setPreviewUrl(pb.files.getURL(exp, exp.receipt))} style={{ color: '#3b82f6', borderColor: '#bfdbfe' }}>
                                                <FaEye /> Voir le ticket
                                            </button>
                                        ) : (
                                            <span style={{ fontSize: '12px', color: '#94a3b8' }}>Aucun ticket</span>
                                        )}
                                    </div>
                                    
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        {isManager && exp.status === 'pending' && (
                                            <>
                                                <button className="w-btn-outline" style={{ color: '#10b981', borderColor: '#dcfce7', background: '#ecfdf5' }} onClick={() => handleUpdateStatus(exp.id, 'approved')}>
                                                    <FaCheck /> Valider
                                                </button>
                                                <button className="w-btn-outline" style={{ color: '#ef4444', borderColor: '#fee2e2', background: '#fef2f2' }} onClick={() => handleUpdateStatus(exp.id, 'rejected')}>
                                                    <FaTimes /> Refuser
                                                </button>
                                            </>
                                        )}
                                        {(isManager || exp.status === 'pending') && (
                                            <button className="w-btn-outline danger" onClick={() => handleDelete(exp.id)}>
                                                <FaTrash />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
                {!isManager && expenses.length > 0 && (
                    <div style={{ marginTop: '20px', paddingBottom: '20px' }}>
                        <button className="w-btn-outline" onClick={handleExportZip} style={{ width: '100%', justifyContent: 'center' }}>
                            <FaFileArchive /> Exporter mon historique (ZIP/PDF)
                        </button>
                    </div>
                )}
            </div>

            {/* Modals */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h3>{isManualEntry ? "Saisie Manuelle" : "Scan Rapide IA"}</h3>
                        <form onSubmit={handleSubmit}>
                            {!isManualEntry && (
                                <div className="form-group">
                                    <label>Photo du justificatif (Ticket)</label>
                                    <div
                                        className={`file-upload-area ${isAnalyzing ? 'analyzing-effect' : ''}`}
                                        onClick={() => !isAnalyzing && document.getElementById('receipt-upload').click()}
                                    >
                                        {isAnalyzing ? (
                                            <div style={{ padding: '20px', color: 'var(--color-primary)' }}>
                                                <FaRobot size={32} style={{ animation: 'pulse 1s infinite' }} />
                                                <p style={{ fontWeight: 800, marginTop: '10px' }}>Analyse IA en cours...</p>
                                            </div>
                                        ) : (
                                            <div style={{ padding: '20px' }}>
                                                <FaCamera size={32} style={{ marginBottom: '10px', color: 'var(--color-primary)' }} />
                                                <div style={{ fontWeight: 800 }}>{newExpense.receipt ? "✅ Ticket chargé" : "Scanner un justificatif"}</div>
                                                <div style={{ fontSize: '12px', opacity: 0.7 }}>{newExpense.receipt ? newExpense.receipt.name : "Cliquez pour capturer"}</div>
                                            </div>
                                        )}
                                        <input type="file" id="receipt-upload" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleFileChange} />
                                    </div>
                                </div>
                            )}

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                <div className="form-group">
                                    <label>Montant TTC (€)</label>
                                    <input type="number" step="0.01" required value={newExpense.amount} onChange={e => setNewExpense({ ...newExpense, amount: e.target.value })} style={{ fontSize: '1.4rem', fontWeight: 900, color: 'var(--color-primary)' }} />
                                </div>
                                <div className="form-group">
                                    <label>Catégorie</label>
                                    <select value={newExpense.category} onChange={e => setNewExpense({ ...newExpense, category: e.target.value })}>
                                        <option value="repas">🍽️ Repas</option>
                                        <option value="essence">⛽ Carburant</option>
                                        <option value="materiel">🔨 Matériel</option>
                                        <option value="autre">📝 Autre</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Description / Commerçant</label>
                                <input type="text" placeholder="Ex: Leroy Merlin..." value={newExpense.description} onChange={e => setNewExpense({ ...newExpense, description: e.target.value })} />
                            </div>

                            {isManualEntry && (
                                <div className="form-group">
                                    <label>Justificatif (Optionnel)</label>
                                    <div className="file-upload-area" onClick={() => document.getElementById('manual-upload').click()} style={{ padding: '15px' }}>
                                        <div style={{ fontWeight: 800, fontSize: '13px' }}>{newExpense.receipt ? "✅ Ticket chargé" : "Ajouter une photo"}</div>
                                        <input type="file" id="manual-upload" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => setNewExpense({ ...newExpense, receipt: e.target.files[0] })} />
                                    </div>
                                </div>
                            )}

                            <div className="modal-actions" style={{ marginTop: '20px' }}>
                                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Annuler</button>
                                <button type="submit" className="btn-save" disabled={submitting || isAnalyzing}>{submitting ? "Envoi..." : "Valider"}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {previewUrl && (
                <div className="modal-overlay" onClick={() => setPreviewUrl(null)}>
                    <div className="modal-content" style={{ padding: 0, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
                        <div style={{ background: '#0f172a', color: '#fff', padding: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0 }}>Justificatif</h3>
                            <div style={{ display: 'flex', gap: '15px' }}>
                                <a href={previewUrl} download style={{ color: '#fff', fontSize: '18px' }}><FaDownload /></a>
                                <button onClick={() => setPreviewUrl(null)} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer' }}>×</button>
                            </div>
                        </div>
                        <img src={previewUrl} alt="Justificatif" style={{ width: '100%', height: 'auto', maxHeight: '70vh', objectFit: 'contain', display: 'block', background: '#000' }} />
                    </div>
                </div>
            )}

            <style>{`
                .expenses-page { min-height: 100vh; background: #f8fafc; margin-top: -20px; padding-bottom: 80px; }
                .w-page-header { padding: 30px 20px 15px; display: flex; justify-content: space-between; align-items: flex-end; }
                .w-page-title { font-size: 22px; font-weight: 900; color: #0f172a; margin: 0; }
                .w-page-subtitle { font-size: 13px; color: #94a3b8; margin: 4px 0 0; }

                .stats-grid { display: grid; gap: 10px; }
                .stat-card { background: #fff; border-radius: 12px; padding: 12px; border: 1px solid #f1f5f9; border-left: 4px solid #3b82f6; display: flex; flex-direction: column; }
                .stat-label { font-size: 10px; font-weight: 800; text-transform: uppercase; color: #64748b; margin-bottom: 4px; }
                .stat-value { font-size: 18px; font-weight: 900; color: #0f172a; }
                .stat-sub { font-size: 10px; opacity: 0.5; font-weight: 700; margin-top: 2px; }

                .w-btn-full { padding: 14px; border-radius: 12px; border: none; font-weight: 800; color: #fff; display: flex; justify-content: center; align-items: center; gap: 8px; cursor: pointer; transition: transform 0.1s; }
                .w-btn-full:active { transform: scale(0.98); }

                .w-cards-list { padding: 0 20px; }
                .w-date-header { font-size: 14px; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 15px; }

                .w-card { background: #fff; border-radius: 16px; padding: 16px; border: 1px solid #f1f5f9; }
                .w-card-header { display: flex; justify-content: space-between; align-items: flex-start; }
                .w-badge { padding: 4px 8px; border-radius: 6px; font-size: 10px; font-weight: 800; text-transform: uppercase; }
                
                .w-card-footer { margin-top: 15px; padding-top: 15px; border-top: 1px solid #f8fafc; display: flex; }
                .w-btn-outline { background: transparent; border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 12px; font-size: 12px; font-weight: 700; display: flex; align-items: center; gap: 6px; cursor: pointer; color: #475569; }
                .w-btn-outline.danger { color: #ef4444; border-color: #fee2e2; }

                .w-empty-state { text-align: center; padding: 40px 20px; background: #fff; border-radius: 16px; border: 1px dashed #cbd5e1; }
                .empty-icon { width: 48px; height: 48px; border-radius: 16px; background: #f8fafc; display: flex; align-items: center; justify-content: center; margin: 0 auto 15px; font-size: 20px; color: #94a3b8; }
                .w-empty-state h3 { margin: 0 0 5px; font-size: 16px; color: #0f172a; }
                .w-empty-state p { margin: 0; font-size: 13px; color: #64748b; }

                .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px; }
                .modal-content { background: #fff; border-radius: 16px; padding: 20px; width: 100%; max-width: 400px; }
                .modal-content h3 { margin: 0 0 20px; font-size: 18px; }
                .form-group { margin-bottom: 15px; }
                .form-group label { display: block; font-size: 12px; font-weight: 800; color: #64748b; margin-bottom: 5px; }
                .form-group input, .form-group select { width: 100%; padding: 10px; border-radius: 8px; border: 1px solid #e2e8f0; font-family: inherit; }
                .file-upload-area { border: 2px dashed #cbd5e1; border-radius: 12px; text-align: center; cursor: pointer; background: #f8fafc; }
                .analyzing-effect { border-color: var(--color-primary); background: #f0f9ff; }
                .modal-actions { display: flex; gap: 10px; }
                .modal-actions button { flex: 1; padding: 12px; border-radius: 8px; font-weight: 800; border: none; cursor: pointer; }
                .btn-cancel { background: #f1f5f9; color: #475569; }
                .btn-save { background: var(--color-primary); color: #fff; }

                /* DARK MODE */
                [data-theme="dark"] .expenses-page { background: var(--color-background, #121212); }
                [data-theme="dark"] .w-page-title, [data-theme="dark"] .stat-value { color: #ffffff; }
                [data-theme="dark"] .w-page-subtitle { color: #a1a1aa; }
                [data-theme="dark"] .stat-card { background: #1e1e1e; border-color: rgba(255,255,255,0.08); }
                [data-theme="dark"] .w-card { background: #1e1e1e; border-color: rgba(255,255,255,0.08); }
                [data-theme="dark"] .w-card-header h3 { color: #ffffff !important; }
                [data-theme="dark"] .cat-icon { background: #27272a !important; color: #e4e4e7 !important; }
                [data-theme="dark"] .w-card-footer { border-top-color: rgba(255,255,255,0.05); }
                [data-theme="dark"] .w-btn-outline { border-color: rgba(255,255,255,0.1); color: #e4e4e7; }
                [data-theme="dark"] .w-btn-outline.danger { border-color: #fee2e2; color: #ef4444; }
                [data-theme="dark"] .w-empty-state { background: #1e1e1e; border-color: rgba(255,255,255,0.1); }
                [data-theme="dark"] .w-empty-state h3 { color: #e4e4e7; }
                [data-theme="dark"] .empty-icon { background: #27272a; }
                [data-theme="dark"] .modal-content { background: #1e1e1e; color: #fff; }
                [data-theme="dark"] .form-group input, [data-theme="dark"] .form-group select { background: #27272a; border-color: #3f3f46; color: #fff; }
                [data-theme="dark"] .file-upload-area { background: #27272a; border-color: #3f3f46; }
                [data-theme="dark"] .btn-cancel { background: #27272a; color: #e4e4e7; }
            `}</style>
        </div>
    );
};

export default TabExpenses;
